import React, { useContext, useEffect, useRef, useState } from 'react';
import Freezer from 'freezer-js';
import { isEqual } from 'lodash';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppDataContext } from 'context/appData';
import { ConfigurationDataContext, GlassStructureContext } from 'context/configuration';
import { UiContext } from 'context/ui';
import { useGlassSize } from 'hooks';
import { AddNewType } from 'components/AddNewType';
import BackButton from 'components/Buttons/BackButton';
import { ConfigurationCode } from 'components/ConfigurationCode';
import { GlassConfig } from 'components/GlassConfig';
import GlassConfigUtil from 'components/GlassConfig/GlassConfigUtil';
import { SaveAsSection } from 'components/SaveAsSection';
import { ScrollChevrons } from 'components/ScrollChevrons';
import { TotalInfo } from 'components/TotalInfo';
import { UploadedDocuments } from 'components/UploadedDocuments';
import { YourReference } from 'components/YourReference';
import { getGlassSizes } from 'domain/glass';
import { generateUniqueId, getCurrentDate } from 'utils';

const CartPage = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { configuratorData, langApp } = useContext(AppDataContext);
	const { configuration, totalGlassSizesSum, setConfiguration, loadConfigurationToFirebase } =
		useContext(ConfigurationDataContext);
	const { showPrice, uiHandlerRef } = useContext(GlassStructureContext);
	const { cartPageActionRef } = useContext(UiContext);
	const { t } = useTranslation('cartPage');
	const [frozenConfigurationData, setFrozenConfigurationData] = useState(null);
	const [scrollIntoView, setScrollIntoView] = useState(null);
	const currentDateRef = useRef(getCurrentDate());
	const continueBtnRef = useRef(null);
	const freezerRef = useRef();
	const activeRowCellRef = useRef({ glassTypeId: null, rowId: null, cellIndex: null }); // points to the last edited cell
	const activeInputRowCellRef = useRef({ glassTypeId: null, rowId: null, cellIndex: null }); // points to the cell that should show an input field (editing)
	const depsRef = useRef({});
	const lastActionRef = useRef(null);
	const configurationRef = useRef(null); // tracks setConfiguration() calls from inside this file so outside calls can be identified
	const rerenderFrozenConfigurationDataRef = useRef(false);

	useGlassSize(frozenConfigurationData);

	// Keep track of dependency changes for the freezerRef listeners
	useEffect(() => {
		depsRef.current = {
			_showPrice: showPrice,
			_configuratorData: configuratorData,
			_frozenConfigurationData: frozenConfigurationData,
			_loadConfigurationToFirebase: loadConfigurationToFirebase,
			_setConfiguration: c => {
				configurationRef.current = c;
				return setConfiguration(c);
			},
			_setFrozenConfigurationData: setFrozenConfigurationData,
			_setScrollIntoView: setScrollIntoView,
		};
	}, [
		showPrice,
		configuratorData,
		frozenConfigurationData,
		loadConfigurationToFirebase,
		setConfiguration,
		setFrozenConfigurationData,
		setScrollIntoView,
	]);

	useEffect(() => {
		if (configuration && !freezerRef.current) {
			const freezer = new Freezer({ json: configuration });
			freezerRef.current = freezer;
			const addNewPos = ({ glassTypeId, inactive, setActiveCell }) => {
				const glassType = freezerRef.current.get().json.glassTypes.find(o => o.id === glassTypeId);
				const lastPos = glassType.positions.at(-1);
				let targetRowId;
				if (lastPos?.inactive) {
					if (!inactive) {
						console.log(
							`frz add new pos: removing inactive marker from ${glassTypeId}:${lastPos.id}`
						);
						lastActionRef.current ||= 'addNewPosition';
						rerenderFrozenConfigurationDataRef.current = true;
						lastPos.set('inactive', false);
						targetRowId = lastPos.id;
					}
					if (setActiveCell && targetRowId) {
						// Emit setActiveInputCell after the state update completes
						setTimeout(() => {
							freezerRef.current.emit('setActiveInputCell', {
								glassTypeId,
								rowId: targetRowId,
								cellIndex: 0,
							});
						}, 0);
					}
					return undefined;
				}

				// Skip if auto-creating and last position is already empty (just activated by TAB)
				if (inactive && lastPos && !lastPos.row[0].value && !lastPos.row[1].value && !lastPos.row[2].value) {
					return undefined;
				}

				const newPosId = generateUniqueId();
				console.log(`frz add new pos rowId=${newPosId}`);
				const frozenJson = freezerRef.current.get().json;
				const thawed = frozenJson.transact();
				thawed.glassTypes
					.find(o => o.id === glassTypeId)
					.positions.push({
						id: newPosId,
						inactive,
						row: [
							{ name: 'length', value: '' },
							{ name: 'width', value: '' },
							{ name: 'quantity', value: '' },
							{ name: 'form', value: 'unchecked' },
							{ name: 'cantonalInsurance', value: '' },
							{ name: 'notes', value: '' },
						],
					});
				lastActionRef.current ||= 'addNewPosition';
				rerenderFrozenConfigurationDataRef.current = true;
				if (setActiveCell) {
					// Emit setActiveInputCell after the state update completes
					setTimeout(() => {
						freezerRef.current.emit('setActiveInputCell', {
							glassTypeId,
							rowId: newPosId,
							cellIndex: 0,
						});
					}, 0);
				}
				return frozenJson.run();
			};

			const maybeDeactivateRow = (_glassTypeId, _rowId) => {
				const frozenJson = freezerRef.current.get().json;
				const gt = frozenJson.glassTypes.find(o => o.id === _glassTypeId);
				if (!gt) return;
				const pos = gt.positions.find(r => r.id === _rowId);
				if (!pos || pos.inactive) return;
				if (pos.row[0].value || pos.row[1].value || pos.row[2].value) return;
				console.log(`frz deactivating empty row ${_glassTypeId}:${_rowId}`);
				lastActionRef.current = 'addNewPosition';
				rerenderFrozenConfigurationDataRef.current = true;
				pos.set('inactive', true);
			};

			depsRef.current.listeners = {
				update: frozenState => {
					const { _showPrice, _configuratorData, _setFrozenConfigurationData, _setScrollIntoView } =
						depsRef.current;
					const { json: frozenJsonState } = frozenState;
					if (
						lastActionRef.current === 'addNewPosition' ||
						lastActionRef.current === 'updateUserData'
					) {
						lastActionRef.current = null;
						_setFrozenConfigurationData(frozenJsonState);
						rerenderFrozenConfigurationDataRef.current = false;
						return;
					}

					const { glassTypeId, rowId } = activeRowCellRef.current;
					const gt = frozenJsonState.glassTypes;
					const glassTypeIndex = gt.findIndex(o => o.id === glassTypeId);
					const pos = glassTypeIndex >= 0 && gt[glassTypeIndex].positions;
					const rowIndex = pos && pos.findIndex(r => r.id === rowId);

					const totals = getGlassSizes({
						glassTypes: frozenJsonState.glassTypes.toJS(),
						showPrice: _showPrice,
						configuratorData: _configuratorData,
						glassDb: uiHandlerRef.current.glassDB,
					});

					if (!isEqual(frozenJsonState.totals, totals)) {
						const thawed = frozenJsonState.transact();
						thawed.totals = totals;
						thawed.amountGlassTypes = frozenJsonState.glassTypes.length;
						thawed.lastEdited = currentDateRef.current;
						thawed.lastEditedDateInMilliseconds = Date.now();

						lastActionRef.current = 'update';
						_setFrozenConfigurationData(frozenJsonState.run());
					} else {
						freezerRef.current.emit('cloudUpload', frozenJsonState);
						if (cartPageActionRef.current?.glassId) {
							_setScrollIntoView(`glass_config_${cartPageActionRef.current.glassId}`);
						}
						if (rerenderFrozenConfigurationDataRef.current) {
							_setFrozenConfigurationData(frozenJsonState);
						}
					}

					if (glassTypeId && rowId) {
						// check if we need to add an empty row if the current one is full
						if (
							pos &&
							pos[rowIndex] &&
							pos[rowIndex].row[0].value &&
							pos[rowIndex].row[1].value &&
							pos[rowIndex].row[2].value
						) {
							const maybeFrozenConfiguration = addNewPos({
								glassTypeId: gt[glassTypeIndex].id,
								inactive: true,
							});
							if (maybeFrozenConfiguration) {
								_setFrozenConfigurationData(maybeFrozenConfiguration);
							}
						}
					}
					rerenderFrozenConfigurationDataRef.current = false;
					lastActionRef.current = null;
				},

				cloudUpload: currentState => {
					const { _loadConfigurationToFirebase, _setConfiguration } = depsRef.current;

					const config = currentState.toJS();
					_setConfiguration(config);
					_loadConfigurationToFirebase(config, false);
				},

				addNewPosition: addNewPos,

				removeGlassType: ({ glassTypeId }) => {
					const frozenJson = freezerRef.current.get().json;
					const thawed = frozenJson.transact();
					const idx = thawed.glassTypes.findIndex(o => o.id === glassTypeId);
					if (idx < 0) {
						console.log(`GlassTypeId ${glassTypeId} not found`);
						return;
					}
					thawed.glassTypes.splice(idx, 1);
					lastActionRef.current = 'removeGlassType';
					rerenderFrozenConfigurationDataRef.current = true;
					frozenJson.run();
				},

				removePosition: ({ glassTypeId, rowId }) => {
					const frozenJson = freezerRef.current.get().json;
					const thawed = frozenJson.transact();
					const glassType = thawed.glassTypes.find(o => o.id === glassTypeId);
					const idx = glassType?.positions.findIndex(r => r.id === rowId);
					if (idx < 0) {
						console.log(`GlassTypeId ${glassTypeId} not found`);
						return;
					}
					glassType.positions.splice(idx, 1);
					lastActionRef.current = 'removePosition';
					rerenderFrozenConfigurationDataRef.current = true;
					frozenJson.run();
				},

				translateConfiguration: lang => {
					const frozenJson = freezerRef.current.get().json;
					const thawed = frozenJson.transact();
					thawed.glassTypes.forEach(g =>
						Object.assign(g, GlassConfigUtil.translate(g, lang, depsRef.current.uiHandlerRef))
					);
					lastActionRef.current = 'translateConfiguration';
					rerenderFrozenConfigurationDataRef.current = true;
					frozenJson.run();
				},

				setActiveInputCell: ({ glassTypeId, rowId, cellIndex }) => {
					activeInputRowCellRef.current = { glassTypeId, rowId, cellIndex };
				},

				setCellValue: (cellValue, { glassTypeId, rowId, cellIndex }) => {
					const frozenJson = freezerRef.current.get().json;
					activeRowCellRef.current = { glassTypeId, rowId, cellIndex };
					const gtIndex = frozenJson.glassTypes.findIndex(o => o.id === glassTypeId);
					const posIndex = frozenJson.glassTypes[gtIndex].positions.findIndex(r => r.id === rowId);
					if (posIndex < 0) {
						console.log(`Position ${glassTypeId}:${rowId}[${cellIndex}] doesn't exist`);
						return;
					}

					lastActionRef.current = 'setCellValue';
					rerenderFrozenConfigurationDataRef.current = true;
					let pos = frozenJson.glassTypes[gtIndex].positions[posIndex];
					if (pos.inactive) {
						console.log(`frz setCellValue marking last pos active ${glassTypeId}:${rowId}`);
						pos = pos.pivot().set('inactive', false); // add a pivot to be able to modify multiple children
					}
					pos.row[cellIndex].set('value', cellValue);
				},

				updateUserData: _configuration => {
					console.log(`frz update user data`);
					const frozenJson = freezerRef.current.get().json;
					const thawed = frozenJson.transact();
					thawed.companyId = _configuration.companyId;
					thawed.createdUserId = _configuration.createdUserId;
					thawed.lastEditedUserEmail = _configuration.lastEditedUserEmail;
					thawed.lastEdited = _configuration.lastEdited;
					thawed.lastEditedDateInMilliseconds = _configuration.lastEditedDateInMilliseconds;
					thawed.lastEditedUserId = _configuration.lastEditedUserId;
					lastActionRef.current = 'updateUserData';
					rerenderFrozenConfigurationDataRef.current = true;
					frozenJson.run();
				},

				onInput: ({ glassTypeId, rowId, cellIndex }) => {
					const prev = activeInputRowCellRef.current;
					if (prev.rowId && prev.rowId !== rowId) {
						maybeDeactivateRow(prev.glassTypeId, prev.rowId);
					}
					activeInputRowCellRef.current = { glassTypeId, rowId, cellIndex };
				},
				onBlur: ({ glassTypeId, rowId, cellIndex }) => {
					if (
						activeInputRowCellRef.current.glassTypeId === glassTypeId &&
						activeRowCellRef.current.rowId === rowId &&
						activeRowCellRef.current.cellIndex === cellIndex
					) {
						activeInputRowCellRef.current = { glassTypeId: null, rowId: null, cellIndex: null };
					} else {
						console.warn(`frz blur for non-active field ${glassTypeId}:${rowId}:${cellIndex}`);
					}
					maybeDeactivateRow(glassTypeId, rowId);
				},
			};

			/* register */
			Object.entries(depsRef.current.listeners).forEach(([e, fn]) => freezer.on(e, fn));

			const frozenJsonState = freezerRef.current.get().json;
			setFrozenConfigurationData(frozenJsonState);
			const scrollTo = cartPageActionRef.current?.glassId || location.state?.scrollTo;
			if (scrollTo) {
				setScrollIntoView(`glass_config_${scrollTo}`);
			}
		} else if (configuration && configuration !== configurationRef.current) {
			// check if configuration was modified externally, e.g. by logging in
			if (
				(configuration.companyId && !frozenConfigurationData.companyId) ||
				(configuration.createdUserId && !frozenConfigurationData.createdUserId) ||
				configuration.lastEditedDateInMilliseconds >
					frozenConfigurationData.lastEditedDateInMilliseconds
			) {
				freezerRef.current.emit('updateUserData', configuration);
				// } else {
				//  This is sometimes just an out-of-sync update where last edited time is older...
				// 	console.warn("configuration changed but it's not the company ID or last edited time");
			}
		}

		return () => {
			if (freezerRef.current?.listeners) {
				Object.entries(depsRef.current.listeners).forEach(([e, fn]) =>
					freezerRef.current.off(e, fn)
				);
			}
		};
	}, [
		cartPageActionRef,
		configuration,
		configuratorData,
		frozenConfigurationData,
		location.state?.scrollTo,
		navigate,
		loadConfigurationToFirebase,
		setConfiguration,
		showPrice,
		uiHandlerRef,
	]);

	useEffect(() => {
		if (scrollIntoView) {
			const ref = document.getElementById(scrollIntoView);
			const header = document.querySelector('header');
			if (ref && header) {
				const elementPosition = ref.getBoundingClientRect().top + window.scrollY;
				const offsetPosition = elementPosition - header.offsetHeight * 1.1;
				window.scrollTo({ behavior: 'smooth', top: offsetPosition });
				cartPageActionRef.current.glassId = undefined;
			}
		}
	}, [cartPageActionRef, scrollIntoView]);

	useEffect(() => {
		const lang = langApp === 'en' ? 'en_US' : langApp;
		if (
			!frozenConfigurationData ||
			!frozenConfigurationData.glassTypes ||
			!frozenConfigurationData.glassTypes.length ||
			frozenConfigurationData.glassTypes.every(g => g.lang === lang)
		)
			return;

		freezerRef.current.emit('translateConfiguration', lang);
	}, [frozenConfigurationData, langApp]);

	if (!configuration) {
		return (
			<section className="section">
				<div className="section__in">
					<div className="section__block">
						<h1 className="section__title section__center">{t('cartIsEmpty')}</h1>
						<div className="section__center">
							<BackButton buttonText={t('orderConfirmationPage:backToStart')} navigateToUrl="/" />
						</div>
					</div>
				</div>
			</section>
		);
	}

	return (
		<section className="section">
			<div className="section__in">
				{frozenConfigurationData ? (
					<>
						{frozenConfigurationData.glassTypes && frozenConfigurationData.glassTypes.length
							? frozenConfigurationData.glassTypes.map(glassData => {
									return (
										<div
											className="section__block"
											id={`glass_config_${glassData.id}`}
											key={glassData.id}
										>
											<GlassConfig
												freezerRef={freezerRef}
												activeInputRowCellRef={activeInputRowCellRef}
												configurationData={frozenConfigurationData}
												glassTypeId={glassData.id}
												showPrice={showPrice}
											/>
										</div>
									);
								})
							: null}
						<div className="section__block">
							<AddNewType />
						</div>
						<div className="section__block">
							<TotalInfo {...totalGlassSizesSum} showPrice={showPrice} />
						</div>
						<div className="section__block">
							<YourReference
								parent={frozenConfigurationData}
								values={{
									reference: frozenConfigurationData.reference,
									cantonalInsurance: frozenConfigurationData.cantonalInsurance,
									buildingReference: frozenConfigurationData.buildingReference,
								}}
							/>
						</div>
						<div className="section__block section_uploaded_docs_config_code">
							<UploadedDocuments uploadedDocuments={frozenConfigurationData.uploadedDocuments} />
							<ConfigurationCode code={frozenConfigurationData.code} />
						</div>
						<SaveAsSection isCartPage continueBtnRef={continueBtnRef} />
						<ScrollChevrons elRef={continueBtnRef} />
					</>
				) : null}
			</div>
		</section>
	);
};

export default CartPage;
